
import { AdminUser, Reward, Address } from '../domain/types';
import { COLLECTIONS } from '../domain/collections';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { CacheInvalidator } from '../utils/cacheInvalidation';
import { PAGINATION, REPOSITORIES, TIME, VALIDATION } from '../domain/constants';
import {
    collection,
    getDocs,
    getDoc,
    doc,
    updateDoc,
    runTransaction,
    arrayUnion,
    arrayRemove,
    setDoc,
    query,
    where,
    limit,
    orderBy,
    startAfter,
    increment,
    QueryDocumentSnapshot,
    DocumentData
} from "firebase/firestore";
import { BaseRepository, BaseEntity, PaginatedResult } from '../domain/services/BaseRepository';

interface UserEntity extends AdminUser, BaseEntity {}

class UserRepository extends BaseRepository<UserEntity> {
    protected collectionName = COLLECTIONS.USERS;

    protected toEntity(doc: QueryDocumentSnapshot): UserEntity {
        const data = doc.data() as UserEntity;
        return {
            id: doc.id,
            name: data.name || 'Usuario',
            email: data.email || '',
            avatar: data.avatar || 'https://ui-avatars.com/api/?name=User',
            points: data.points ?? 0,
            walletBalance: data.walletBalance ?? 0,
            level: data.level ?? 1,
            role: data.role || 'user',
            isAdmin: !!data.isAdmin,
            completedTasks: data.completedTasks || [],
            activeRewards: data.activeRewards || [],
            participatingRaffles: data.participatingRaffles || [],
            wonAuctions: data.wonAuctions || [],
            addresses: data.addresses || [],
            joinDate: data.joinDate || new Date().toISOString(),
            totalSpent: data.totalSpent ?? 0,
            ordersCount: data.ordersCount ?? 0,
            status: data.status || 'active',
            dni: data.dni || '',
            emailVerified: !!data.emailVerified,
            phone: data.phone || '',
            createdAt: data.createdAt || new Date().toISOString(),
            updatedAt: data.updatedAt || new Date().toISOString()
        };
    }

    async getAllUsers(lastVisible: QueryDocumentSnapshot<DocumentData> | null = null, pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE): Promise<{ users: AdminUser[], lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
        try {
            let q;
            if (lastVisible) {
                q = query(
                    collection(db, this.collectionName),
                    orderBy("joinDate", "desc"),
                    startAfter(lastVisible),
                    limit(pageSize)
                );
            } else {
                q = query(
                    collection(db, this.collectionName),
                    orderBy("joinDate", "desc"),
                    limit(pageSize)
                );
            }

            const snapshot = await getDocs(q);
            const users = snapshot.docs.map(doc => {
                return this.mapUser(doc as QueryDocumentSnapshot<DocumentData>);
            });

            const lastDoc = (snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot<DocumentData>) || null;
            return { users, lastDoc };
        } catch (error) {
            logger.error('[UserRepository] Error fetching users', error);
            return { users: [], lastDoc: null };
        }
    }

    // Legacy method for backward compatibility
    private mapUser(docSnap: QueryDocumentSnapshot): AdminUser {
        return this.toEntity(docSnap);
    }

    async searchUsers(queryStr: string): Promise<AdminUser[]> {
        try {
            if (!queryStr || queryStr.length < VALIDATION.SEARCH_MIN_LENGTH) return [];
            const lowerQ = queryStr.toLowerCase();

            // Limit to most recent 100 for prototype search efficiency
            const q = query(collection(db, this.collectionName), limit(REPOSITORIES.USER_SEARCH_LIMIT));
            const snapshot = await getDocs(q);
            
            return snapshot.docs
                .map(docSnap => this.mapUser(docSnap as QueryDocumentSnapshot<DocumentData>))
                .filter(u =>
                    u.name.toLowerCase().includes(lowerQ) ||
                    u.email.toLowerCase().includes(lowerQ) ||
                    u.dni.includes(lowerQ)
                ).slice(0, PAGINATION.SEARCH_RESULTS_LIMIT);
        } catch (error) {
            logger.error('[UserRepository] User search failed', error);
            return [];
        }
    }

    async getUserByEmail(email: string): Promise<AdminUser | undefined> {
        try {
            const q = query(collection(db, this.collectionName), where("email", "==", email), limit(1));
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                return this.mapUser(snapshot.docs[0]);
            }
        } catch (error) {
            logger.error('[UserRepository] Error fetching user by email', error);
        }
        return undefined;
    }

    async getUserById(id: string, retries = 3): Promise<AdminUser | undefined> {
        try {
            const docRef = doc(db, this.collectionName, id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return this.mapUser(docSnap);
            }
        } catch (error) {
            const err = error as { code?: string };
            if (err.code === 'permission-denied' && retries > 0) {
                logger.warn(`⚠️ Permission denied fetching user ${id}. Retrying... (${retries} left)`);
                await new Promise(r => setTimeout(r, TIME.PERMISSION_DENIED_RETRY_DELAY_MS));
                return this.getUserById(id, retries - 1);
            }
            logger.error(`[UserRepository] Error fetching user ${id}`, error);
        }
        return undefined;
    }

    async getUserByDni(dni: string): Promise<AdminUser | undefined> {
        if (!dni) return undefined;
        const q = query(collection(db, this.collectionName), where("dni", "==", dni), limit(1));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            return { ...(snapshot.docs[0].data() as object), id: snapshot.docs[0].id } as AdminUser;
        }
        return undefined;
    }

    // BaseRepository already provides create, update, and delete methods

    // Security: Only used by WalletService internally
    async updateWalletBalance(userId: string, amount: number): Promise<number> {
        const userRef = doc(db, this.collectionName, userId);

        // Optimize: Use increment instead of transaction for atomic updates
        await updateDoc(userRef, { walletBalance: increment(amount) });

        // Fetch updated value
        const userDoc = await getDoc(userRef);
        const newBalance = userDoc.data()?.walletBalance || 0;

        if (newBalance < 0) {
            await updateDoc(userRef, { walletBalance: increment(-amount) });
            const err = new Error("Saldo insuficiente en el servidor");
            logger.error('[UserRepository] Insufficient wallet balance after deduction', err);
            throw err;
        }

        // Invalidate user cache
        CacheInvalidator.invalidateUser(userId);

        return newBalance;
    }

    // Security: Only used by GamificationService internally
    async updatePoints(userId: string, amount: number): Promise<number> {
        const userRef = doc(db, this.collectionName, userId);

        // Optimize: Use increment instead of transaction for atomic updates
        await updateDoc(userRef, { points: increment(amount) });

        // Fetch updated value
        const userDoc = await getDoc(userRef);
        const newPoints = userDoc.data()?.points || 0;

        if (newPoints < 0) {
            await updateDoc(userRef, { points: increment(-amount) });
            const err = new Error("Puntos insuficientes");
            logger.error('[UserRepository] Insufficient points after deduction', err);
            throw err;
        }

        // Invalidate user cache
        CacheInvalidator.invalidateUser(userId);

        return newPoints;
    }

  // Security: Atomic operation for tasks to prevent double-claiming
  async completeSocialTask(userId: string, taskId: string, rewardPoints: number): Promise<number> {
    const userRef = doc(db, this.collectionName, userId);

    const newPoints = await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        const err = new Error("Usuario no encontrado");
        logger.error('[UserRepository] User not found in transaction', err);
        throw err;
      }

      const userData = userDoc.data();
      const completedTasks = userData.completedTasks || [];

      if (completedTasks.includes(taskId)) {
        const err = new Error("Esta tarea ya fue completada y recompensada.");
        logger.error('[UserRepository] Task already completed: ' + taskId, err);
        throw err;
      }

      const points = (userData.points || 0) + rewardPoints;
      transaction.update(userRef, {
        completedTasks: arrayUnion(taskId),
        points: points
      });

      return points;
    });

    // Invalidate user cache after transaction
    CacheInvalidator.invalidateUser(userId);

    return newPoints;
  }

  async addReward(userId: string, reward: Reward): Promise<void> {
    const userRef = doc(db, this.collectionName, userId);
    await updateDoc(userRef, {
      activeRewards: arrayUnion(reward)
    });

    // Invalidate user cache
    CacheInvalidator.invalidateUser(userId);
  }

  async addRaffleParticipation(userId: string, raffleId: string): Promise<void> {
    const userRef = doc(db, this.collectionName, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      const err = new Error("Usuario no encontrado");
      logger.error('[UserRepository] User not found for raffle participation: ' + userId, err);
      throw err;
    }

    const participatingRaffles = userDoc.data().participatingRaffles || [];
    if (participatingRaffles.includes(raffleId)) {
      const err = new Error("Ya estás participando en este sorteo");
      logger.error('[UserRepository] Already participating in raffle: ' + raffleId, err);
      throw err;
    }

    await updateDoc(userRef, {
      participatingRaffles: arrayUnion(raffleId)
    });

    // Invalidate user cache
    CacheInvalidator.invalidateUser(userId);
  }

  // --- Atomic Array Management (Optimization) ---
  async addAddress(userId: string, address: Address): Promise<void> {
    const userRef = doc(db, this.collectionName, userId);
    await updateDoc(userRef, { addresses: arrayUnion(address) });

    // Invalidate user cache
    CacheInvalidator.invalidateUser(userId);
  }

  async removeAddress(userId: string, address: Address): Promise<void> {
    const userRef = doc(db, this.collectionName, userId);
    await updateDoc(userRef, { addresses: arrayRemove(address) });

    // Invalidate user cache
    CacheInvalidator.invalidateUser(userId);
  }

  async updateAddresses(userId: string, addresses: Address[]): Promise<void> {
    const userRef = doc(db, this.collectionName, userId);
    await updateDoc(userRef, { addresses });

    // Invalidate user cache
    CacheInvalidator.invalidateUser(userId);
  }

  async addFavorite(userId: string, productId: string): Promise<void> {
    const userRef = doc(db, this.collectionName, userId);
    await updateDoc(userRef, { favorites: arrayUnion(productId) });

    // Invalidate user cache
    CacheInvalidator.invalidateUser(userId);
  }

  async removeFavorite(userId: string, productId: string): Promise<void> {
    const userRef = doc(db, this.collectionName, userId);
    await updateDoc(userRef, { favorites: arrayRemove(productId) });

    // Invalidate user cache
    CacheInvalidator.invalidateUser(userId);
  }

  async updateFavorites(userId: string, favorites: string[]): Promise<void> {
    const userRef = doc(db, this.collectionName, userId);
    await updateDoc(userRef, { favorites });

    // Invalidate user cache
    CacheInvalidator.invalidateUser(userId);
  }
}

export const userRepository = new UserRepository();
export type { AdminUser };
